import { useState } from "react";
import { Link } from "react-router-dom";
import { useFetch, fmtTime } from "@/lib/api";
import { PageHeader, Loading, ErrorBox, Empty, SevBadge, IncidentStatus } from "@/components/common";
import { useAuth } from "@/context/AuthContext";

const STATUSES = ["new", "investigating", "diagnosed", "awaiting_approval", "remediation_scheduled", "remediation_running", "resolved", "escalated", "closed"];

export default function Incidents() {
  const { orgId } = useAuth();
  const [f, setF] = useState({ status: "", severity: "", q: "", open_only: true });
  const [page, setPage] = useState(1);
  const qs = `status=${f.status}&severity=${f.severity}&q=${encodeURIComponent(f.q)}&open_only=${f.open_only}&page=${page}`;
  const { data, loading, error } = useFetch(`/incidents?${qs}`, [orgId]);
  const pages = data ? Math.max(1, Math.ceil(data.total / 25)) : 1;
  const set = (k, v) => { setF({ ...f, [k]: v }); setPage(1); };
  return (
    <div className="fade-up">
      <PageHeader title="Incidents" crumbs={["Portal", "Incidents"]} />
      <div className="flex flex-wrap gap-3 mb-4 items-center">
        <input placeholder="Search title or host…" value={f.q} onChange={(e) => set("q", e.target.value)} className="border border-gray-300 px-3 py-2 text-sm w-64" data-testid="incidents-search-input" />
        <select value={f.status} onChange={(e) => set("status", e.target.value)} className="border border-gray-300 px-3 py-2 text-sm" data-testid="incidents-status-filter">
          <option value="">Any status</option>{STATUSES.map((s) => <option key={s} value={s}>{s.replaceAll("_", " ")}</option>)}
        </select>
        <select value={f.severity} onChange={(e) => set("severity", e.target.value)} className="border border-gray-300 px-3 py-2 text-sm" data-testid="incidents-severity-filter">
          <option value="">Any severity</option>{["critical", "high", "medium", "low"].map((s) => <option key={s}>{s}</option>)}
        </select>
        <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={f.open_only} onChange={(e) => set("open_only", e.target.checked)} data-testid="incidents-open-only-checkbox" />Open only</label>
      </div>
      <ErrorBox error={error} />
      {loading ? <Loading /> : data.items.length === 0 ? <Empty title="No incidents" text="Incidents are created automatically from agent telemetry and heartbeat monitoring." testid="incidents-empty" /> : (
        <div className="card-flat overflow-x-auto">
          <table className="data-table" data-testid="incidents-table">
            <thead><tr><th>#</th><th>Incident</th><th>Device</th><th>Severity</th><th>Status</th><th>Source</th><th>Occurrences</th><th>First / last observed</th></tr></thead>
            <tbody>{data.items.map((i) => (
              <tr key={i.id} data-testid={`incident-row-${i.id}`}>
                <td className="font-mono text-gray-500">{i.number}</td>
                <td><Link to={`/portal/incidents/${i.id}`} className="font-semibold hover:text-red-600" data-testid={`incident-link-${i.id}`}>{i.title}</Link><div className="text-xs text-gray-400">{i.category}</div></td>
                <td>{i.hostname}</td><td><SevBadge severity={i.severity} /></td><td><IncidentStatus status={i.status} /></td>
                <td className="text-xs">{i.detection_source}</td><td className="font-mono">{i.occurrences}</td>
                <td className="text-xs whitespace-nowrap">{fmtTime(i.first_observed)}<div className="text-gray-400">{fmtTime(i.last_observed)}</div></td>
              </tr>))}</tbody>
          </table>
          <div className="flex justify-between items-center p-3 text-sm text-gray-500"><span>{data.total} incidents</span>
            <div className="flex gap-2"><button disabled={page <= 1} onClick={() => setPage(page - 1)} className="px-3 py-1 border disabled:opacity-40">Prev</button><span>{page}/{pages}</span><button disabled={page >= pages} onClick={() => setPage(page + 1)} className="px-3 py-1 border disabled:opacity-40">Next</button></div></div>
        </div>
      )}
    </div>
  );
}
